<template>
  <section class="testimonials-section">
    <div class="section-container">
      <div class="section-label">آراء الطلاب</div>
      <h2 class="section-title">ماذا يقول طلابنا؟</h2>
    </div>

    <!-- Loading skeletons -->
    <div v-if="loading" class="testimonials-outer">
      <div class="testimonials-track testimonials-track--static">
        <div class="tcard tcard--skeleton" v-for="n in 5" :key="'sk-' + n">
          <div class="tsk tsk-stars"></div>
          <div class="tsk tsk-line"></div>
          <div class="tsk tsk-line tsk-line--short"></div>
          <div class="tcard-author" style="margin-top:auto">
            <div class="tsk tsk-avatar"></div>
            <div style="flex:1"><div class="tsk tsk-name"></div></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!testimonials.length" class="testimonials-empty">
      <div class="te-icon">💬</div>
      <p>لا توجد تقييمات بعد — كن أول من يشارك رأيه!</p>
    </div>

    <!-- Infinite scrolling marquee -->
    <div v-else class="testimonials-outer">
      <div
        class="testimonials-track"
        :style="{ animationDuration: animDuration }"
      >
        <div
          class="tcard"
          v-for="(item, idx) in testimonials"
          :key="idx"
        >
          <div class="tcard-stars">
            <span v-for="s in 5" :key="s" class="tcard-star" :class="s <= item.number_of_stars ? 'tcard-star--on' : 'tcard-star--off'">★</span>
          </div>
          <p class="tcard-text">"{{ item.note }}"</p>
          <div class="tcard-author">
            <div class="tcard-avatar">{{ getInitial(item.student_name) }}</div>
            <div><div class="tcard-name">{{ item.student_name }}</div></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  testimonials: { type: Array, default: () => [] },
  loading: { type: Boolean, default: true },
});

// Marquee math:
//   - Build a `base` set with at least MIN_VISIBLE unique slots.
//     If the API returns fewer items, repeat the source to fill them.
//   - Double the base: [...base, ...base].
//     The animation runs translateX(0) → translateX(-50%).
//     At -50% the view is pixel-identical to the view at 0%
//     because the second half is the same as the first → seamless loop.
//   - With enough base items the two halves never fit simultaneously
//     on screen, so the repetition is invisible.
const MIN_VISIBLE = 8;             // slots needed to fill a typical viewport
const PX_PER_CARD = 324;           // 300px card + 24px gap
const SCROLL_SPEED = 80;           // px/s — tune for feel

const animDuration = computed(() => {
  const src = props.testimonials;
  if (!src.length) return '35s';
  const copies = Math.ceil(MIN_VISIBLE / src.length);
  const baseCount = copies * src.length;
  const seconds = Math.round((baseCount * PX_PER_CARD) / SCROLL_SPEED);
  return `${Math.max(20, seconds)}s`;
});

function getInitial(name) {
  return name ? name.charAt(0) : '؟';
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.section-container { max-width:1200px;margin:0 auto;padding:0 2rem; }
.section-label { display:inline-block;background:rgba(212,175,55,.08);color:#D4AF37;border:1px solid rgba(212,175,55,.2);border-radius:99px;padding:.3rem 1rem;font-size:.8rem;font-weight:700;margin-bottom:1rem; }
.section-title { font-size:clamp(1.6rem,3vw,2.2rem);font-weight:800;color:#F0EDE4;margin-bottom:2.5rem;line-height:1.3; }

.testimonials-section { padding:6rem 0;background:#0A0A0A;overflow:hidden; }
.testimonials-section .section-container { margin-bottom:3rem; }

.testimonials-outer {
  overflow:hidden;
  -webkit-mask:linear-gradient(90deg,transparent,black 10%,black 90%,transparent);
  mask:linear-gradient(90deg,transparent,black 10%,black 90%,transparent);
}
.testimonials-track { display:flex;gap:1.5rem;width:max-content;animation:testimScroll 35s linear infinite;padding:1rem 0; }
.testimonials-track:hover { animation-play-state:paused; }
.testimonials-track--static { animation:none !important;width:auto; }
@keyframes testimScroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }

.tcard { background:#1A1A1A;border-radius:20px;padding:1.75rem;width:300px;flex-shrink:0;box-shadow:0 6px 24px rgba(0,0,0,.4);border:1px solid rgba(212,175,55,.12);display:flex;flex-direction:column;gap:.85rem;transition:transform .3s,box-shadow .3s; }
.tcard:hover { transform:translateY(-6px);box-shadow:0 20px 50px rgba(212,175,55,.15); }
.tcard--skeleton { pointer-events:none; }
.tcard-stars { display:flex;gap:.1rem;font-size:.9rem;letter-spacing:.1rem; }
.tcard-star { font-size:1.1rem;transition:transform .15s; }
.tcard-star--on  { color:#F59E0B; }
.tcard-star--off { color:#E5E7EB; }
.tcard-text { font-size:.9rem;color:rgba(240,237,228,0.75);line-height:1.7;flex:1; }
.tcard-author { display:flex;align-items:center;gap:.75rem;margin-top:.25rem; }
.tcard-avatar { width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,#D4AF37,#C89B2D);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:1rem;flex-shrink:0; }
.tcard-name { font-weight:700;color:#F0EDE4;font-size:.9rem; }

/* Skeleton shimmer */
.tsk { background:linear-gradient(90deg,#f0f0f0 25%,#e6e6e6 50%,#f0f0f0 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;border-radius:8px; }
.tsk-stars { height:16px;width:80px;border-radius:99px; }
.tsk-line  { height:13px;width:95%;margin-top:.5rem; }
.tsk-line--short { width:65%; }
.tsk-avatar { width:40px;height:40px;border-radius:50%;flex-shrink:0; }
.tsk-name  { height:14px;width:100px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Empty state */
.testimonials-empty { text-align:center;padding:3rem 1rem; }
.te-icon { font-size:3rem;margin-bottom:.75rem; }
.testimonials-empty p { color:#6B7280;font-size:.95rem; }
</style>
